import React, { useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Tag,
  Image,
  Row,
  Col,
  Flex,
  Tooltip,
  message,
} from 'antd';
import { useTranslation } from '@refinedev/core';
import { ChangeReq, Section } from '@/api/types/getOnePost';
import { getFileUrl } from '@/Helpers';
import {
  FileTextOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  MessageOutlined,
  CommentOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { ChangeRequestBadge } from '@/components/badges/ChangeRequestBadge';
import { DeleteButton } from '@/components/buttons/delete';
import useAPI from '@/api/useApi';
import { endPoints } from '@/api/configs';
import { getSectionContentHtml } from '@/Helpers/getSectionContentHtml';
import { VideoPlayer } from '@/components/videoPlayer/VideoPlayer';

const { Title, Text } = Typography;

interface SectionCardProps {
  section: Section;
  onAddComment: (sectionId?: number) => void;
  onChangeRequest: (changeRequests: ChangeReq[]) => void;
  reqStatus: string;
  refetch: () => void;
  onEdit?: (section: Section) => void;
  readOnly?: boolean;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  section,
  onAddComment,
  onChangeRequest,
  reqStatus,
  refetch,
  onEdit,
  readOnly = false,
}) => {
  const { translate } = useTranslation();
  const [sectionIdToDelete, setSectionIdToDelete] = useState<string>('0');
  const { useDelete } = useAPI();
  const { mutateAsync, isLoading } = useDelete(
    endPoints.deleteSection(sectionIdToDelete || '0')
  );
  const handleChooseDelete = (id: string) => {
    setSectionIdToDelete(id);
  };
  const handleDelete = () => {
    mutateAsync(
      {},
      {
        onSuccess: () => {
          message.success(translate('section.deleted-successfully'));
          refetch();
          handleChooseDelete('0');
        },
        onError: (error) => {
          message.error(error.message || translate('section.error-deleting'));
        },
      }
    );
  };
  if (!section?.id) return null;
  const contentHtml = getSectionContentHtml(section.content);
  const isEmbed = section.type === 'EMBED';
  return (
    <Card
      className="mb-6 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
      title={
        <Flex align="center" justify="space-between" className="py-2 gap-x-4">
          <div className="flex flex-wrap items-center gap-2">
            <Text strong className="text-base text-wrap sm:text-lg">
              {section.title || translate('sections.untitled')}
            </Text>
            {isEmbed && <Tag color="cyan">{translate('section.embed')}</Tag>}
            {!readOnly && (
              <ChangeRequestBadge
                changeRequests={
                  section.change_requests?.filter(
                    (cr) => cr.status === 'PENDING'
                  ) || []
                }
                onClick={() => onChangeRequest(section.change_requests || [])}
              />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tag color={section.view === 'HORIZONTAL' ? 'green' : 'purple'}>
              {translate(`sections.view.${section.view}`)}
            </Tag>
            {!readOnly &&
              (reqStatus === 'PENDING' || reqStatus === 'CHANGE_REQUESTED') && (
                <>
                  <Button
                    type="primary"
                    icon={<CommentOutlined />}
                    onClick={() => onAddComment(section.id)}
                    className="flex items-center"
                  />
                  {onEdit && (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => onEdit(section)}
                      className="flex items-center"
                    />
                  )}
                </>
              )}
            {!readOnly &&
              reqStatus !== 'APPROVED' &&
              reqStatus !== 'REJECTED' && (
                <DeleteButton
                  id={section.id.toString()}
                  onConfirm={() => handleDelete()}
                  type="primary"
                  loading={isLoading}
                  onClick={() => handleChooseDelete(section.id.toString())}
                  className="flex items-center"
                />
              )}
          </div>
        </Flex>
      }
    >
      <Flex vertical gap="large">
        {contentHtml && (
          <div className="border rounded-lg border-blue-500  p-6 rounded-r-lg">
            <Title level={5} className="mb-4 flex items-center gap-2">
              <FileTextOutlined className="text-secondary" />
              {isEmbed
                ? translate('section.embed')
                : translate('sections.content')}
            </Title>
            <div
              className={
                isEmbed
                  ? 'w-full overflow-hidden [&_iframe]:w-full [&_iframe]:min-h-[500px] [&_iframe]:border-0'
                  : 'prose dark:prose-invert max-w-none'
              }
            >
              <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
            </div>
          </div>
        )}
        {section.description && (
          <div className="border rounded-lg border-blue-500  p-6 rounded-r-lg">
            <Title level={5} className="mb-2 flex items-center gap-2">
              <InfoCircleOutlined className="text-secondary" />
              {translate('sections.description')}
            </Title>
            <div className="prose dark:prose-invert max-w-none">
              <div dangerouslySetInnerHTML={{ __html: section.description }} />
            </div>
          </div>
        )}
        {section.quote && (
          <div className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-6 rounded-r-lg">
            <Title level={5} className="mb-4 flex items-center gap-2">
              <MessageOutlined className="text-secondary" />
              {translate('sections.quote')}
            </Title>
            <div className="prose dark:prose-invert max-w-none italic">
              <div dangerouslySetInnerHTML={{ __html: section.quote }} />
            </div>
          </div>
        )}

        {section.uploads?.length > 0 && (
          <div>
            <Title level={5} className="mb-4 flex items-center gap-2">
              <PictureOutlined className="text-secondary" />
              {translate('sections.uploads')}
            </Title>
            <Row gutter={[16, 16]}>
              {section.uploads.map((upload) => (
                <Col key={upload.id} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    hoverable
                    cover={
                      (upload.base_type === 'IMAGE' && (
                        <Image
                          src={getFileUrl(upload.id.toString())}
                          alt={upload.file_name}
                          className="object-cover h-48 w-full"
                        />
                      )) ||
                      (upload.base_type === 'VIDEO' && (
                        <VideoPlayer
                          uploadId={upload.id}
                          fileName={upload.file_name}
                        />
                      ))
                    }
                    className="overflow-hidden"
                  >
                    <Tooltip title={upload.file_name}>
                      <Text className="block text-sm truncate">
                        {upload.file_name}
                      </Text>
                      {upload.description && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: upload.description,
                          }}
                          className="text-sm text-gray-500"
                        />
                      )}
                    </Tooltip>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Flex>
    </Card>
  );
};
